/**
 * Blok piece catalogue. Every orientation is its own piece (the player cannot rotate), which is
 * how the reference game works: the bag decides which orientations you get.
 * Pure TS: no DOM, no Phaser, no Math.random.
 */
export const SIDE = 8;

export type Cell = readonly [r: number, c: number];

export interface PieceDef {
  id: string;
  family: string;
  /** Tile color index (into the kit COLORS palette). */
  color: number;
  cells: readonly Cell[];
  w: number;
  h: number;
  size: number;
  /** One bitmask per piece row; bit c = column c. Used by the fast placement/solver code. */
  rows: readonly number[];
}

interface Shape {
  family: string;
  color: number;
  art: readonly string[];
}

const SHAPES: Record<string, Shape> = {
  dot: { family: "dot", color: 0, art: ["#"] },
  i2h: { family: "i2", color: 3, art: ["##"] },
  i2v: { family: "i2", color: 3, art: ["#", "#"] },
  i3h: { family: "i3", color: 3, art: ["###"] },
  i3v: { family: "i3", color: 3, art: ["#", "#", "#"] },
  i4h: { family: "i4", color: 3, art: ["####"] },
  i4v: { family: "i4", color: 3, art: ["#", "#", "#", "#"] },
  i5h: { family: "i5", color: 3, art: ["#####"] },
  i5v: { family: "i5", color: 3, art: ["#", "#", "#", "#", "#"] },
  o2: { family: "o2", color: 1, art: ["##", "##"] },
  // 3-cell corners
  c3a: { family: "c3", color: 6, art: ["##", "#."] },
  c3b: { family: "c3", color: 6, art: ["##", ".#"] },
  c3c: { family: "c3", color: 6, art: ["#.", "##"] },
  c3d: { family: "c3", color: 6, art: [".#", "##"] },
  // L / J tetrominoes
  l4a: { family: "l4", color: 5, art: ["#.", "#.", "##"] },
  l4b: { family: "l4", color: 5, art: ["###", "#.."] },
  l4c: { family: "l4", color: 5, art: ["##", ".#", ".#"] },
  l4d: { family: "l4", color: 5, art: ["..#", "###"] },
  j4a: { family: "l4", color: 5, art: [".#", ".#", "##"] },
  j4b: { family: "l4", color: 5, art: ["#..", "###"] },
  j4c: { family: "l4", color: 5, art: ["##", "#.", "#."] },
  j4d: { family: "l4", color: 5, art: ["###", "..#"] },
  // T
  t4a: { family: "t4", color: 4, art: ["###", ".#."] },
  t4b: { family: "t4", color: 4, art: [".#.", "###"] },
  t4c: { family: "t4", color: 4, art: ["#.", "##", "#."] },
  t4d: { family: "t4", color: 4, art: [".#", "##", ".#"] },
  // S / Z
  s4a: { family: "s4", color: 2, art: [".##", "##."] },
  s4b: { family: "s4", color: 2, art: ["#.", "##", ".#"] },
  z4a: { family: "s4", color: 2, art: ["##.", ".##"] },
  z4b: { family: "s4", color: 2, art: [".#", "##", "#."] },
  // 3x3 corner (5 cells)
  l5a: { family: "l5", color: 7, art: ["#..", "#..", "###"] },
  l5b: { family: "l5", color: 7, art: ["###", "#..", "#.."] },
  l5c: { family: "l5", color: 7, art: ["###", "..#", "..#"] },
  l5d: { family: "l5", color: 7, art: ["..#", "..#", "###"] },
  // 2x3 rectangles (6) and the 3x3 square (9)
  r23h: { family: "r23", color: 1, art: ["###", "###"] },
  r23v: { family: "r23", color: 1, art: ["##", "##", "##"] },
  sq3: { family: "sq3", color: 7, art: ["###", "###", "###"] },
};

function build(id: string, s: Shape): PieceDef {
  const cells: Cell[] = [];
  s.art.forEach((line, r) => [...line].forEach((ch, c) => ch === "#" && cells.push([r, c])));
  const h = s.art.length;
  const w = Math.max(...s.art.map((l) => l.length));
  const rows = s.art.map((line) => [...line].reduce((m, ch, c) => (ch === "#" ? m | (1 << c) : m), 0));
  return { id, family: s.family, color: s.color, cells, w, h, size: cells.length, rows };
}

export const PIECE_LIST: readonly PieceDef[] = Object.entries(SHAPES).map(([id, s]) => build(id, s));
export const PIECES: Readonly<Record<string, PieceDef>> = Object.fromEntries(PIECE_LIST.map((p) => [p.id, p]));
export const PIECE_IDS: readonly string[] = PIECE_LIST.map((p) => p.id);

export function piece(id: string): PieceDef {
  const p = PIECES[id];
  if (!p) throw new Error(`unknown piece ${id}`);
  return p;
}
